import React from "react";
import { FaXmark, FaEye } from "react-icons/fa6";
import { HiOutlineDocumentText } from "react-icons/hi2";

const NoWrongAnswersModal = ({
  isOpen,
  onClose,
  onUseShowFirstLetter,
  onUseShowAnswerPattern,
  teamName,
  themeColors = {
    primary: "#29a6d6",
    primaryHover: "#1e7ba6",
    background: "#eaf6fb",
    border: "#bfe4f3",
  },
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100 overflow-y-auto"
        style={{ direction: "rtl" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-8 py-6 border-b-2 rounded-t-2xl"
          style={{
            backgroundColor: themeColors.background,
            borderColor: themeColors.border,
          }}
        >
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-full hover:bg-gray-100"
          >
            <FaXmark size={24} />
          </button>
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
              style={{ backgroundColor: themeColors.primary }}
            >
              <span className="text-white text-xl">⚠️</span>
            </div>
            <h2
              className="text-2xl font-bold"
              style={{ color: themeColors.primary }}
            >
              تنبيه هام
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Left Side - Warning and Team Info */}
            <div className="space-y-6">
              {/* Warning Message */}
              <div
                className="p-6 rounded-lg"
                style={{ backgroundColor: themeColors.background }}
              >
                <p className="text-gray-800 text-xl leading-relaxed text-center">
                  لا توجد إجابات خاطئة لهذا السؤال لعرض{" "}
                  <strong>خيارات متعددة</strong>
                </p>
              </div>

              {/* Team Info */}
              {teamName && (
                <div className="text-center">
                  <p
                    className="text-xl font-semibold"
                    style={{ color: themeColors.primary }}
                  >
                    فريق: {teamName}
                  </p>
                </div>
              )}

              {/* Warning Note */}
              <div
                className="p-4 rounded-lg border-l-4"
                style={{
                  backgroundColor: "#fef3cd",
                  borderColor: "#f59e0b",
                }}
              >
                <p className="text-sm text-gray-700 text-center leading-relaxed">
                  <strong>ملاحظة:</strong> استخدام أي من هذين البديلين سيُحتسب
                  كاستخدام مساعدة الخيارات المتعددة
                </p>
              </div>
            </div>

            {/* Right Side - Alternative Options */}
            <div>
              <h3
                className="text-xl font-bold mb-6 text-center"
                style={{ color: themeColors.primary }}
              >
                بدائل مساعدة الخيارات المتعددة:
              </h3>

              <div className="space-y-4">
                {/* Show First Letter Option */}
                <button
                  onClick={onUseShowFirstLetter}
                  className="w-full p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-xl group"
                  style={{
                    borderColor: themeColors.border,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = themeColors.background;
                    e.target.style.borderColor = themeColors.primary;
                    e.target.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "white";
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: themeColors.primary }}
                      >
                        <FaEye className="text-white" size={20} />
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-800 text-lg">
                          إظهار أول حرف من الإجابة
                        </p>
                        <p className="text-gray-600">
                          يكشف الحرف الأول كمساعدة
                        </p>
                      </div>
                    </div>
                    <div className="text-3xl text-gray-400 group-hover:text-gray-600 transition-colors">
                      ←
                    </div>
                  </div>
                </button>

                {/* Show Answer Pattern Option */}
                <button
                  onClick={onUseShowAnswerPattern}
                  className="w-full p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-xl group"
                  style={{
                    borderColor: themeColors.border,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = themeColors.background;
                    e.target.style.borderColor = themeColors.primary;
                    e.target.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "white";
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: themeColors.primary }}
                      >
                        <HiOutlineDocumentText
                          className="text-white"
                          size={20}
                        />
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-800 text-lg">
                          إظهار نمط الإجابة
                        </p>
                        <p className="text-gray-600">
                          يُظهر عدد الأحرف والكلمات
                        </p>
                      </div>
                    </div>
                    <div className="text-3xl text-gray-400 group-hover:text-gray-600 transition-colors">
                      ←
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-8 py-5 border-t-2 rounded-b-2xl"
          style={{
            backgroundColor: themeColors.background,
            borderColor: themeColors.border,
          }}
        >
          <div className="max-w-md mx-auto">
            <button
              onClick={onClose}
              className="w-full py-4 rounded-xl font-bold text-lg transition-colors"
              style={{
                backgroundColor: themeColors.primary,
                color: "white",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = themeColors.primaryHover;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = themeColors.primary;
              }}
            >
              إغلاق
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoWrongAnswersModal;
